import { CommonModule, DatePipe } from '@angular/common';
import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicTableComponent } from 'app/shared/components/dynamic-table/dynamic-table.component';
import { CaseFormSectionContainerComponent } from 'app/shared/components/forms/case-form-section-container/case-form-section-container.component';
import { UploadAttachmentsComponent } from 'app/shared/components/forms/fields/upload-attachments/upload-attachments.component';
import { HelpModalComponent } from 'app/shared/components/help-modal/help-modal.component';
import { DOCUMENT_LIBRARY_TABLE_CONFIG, DOCUMENT_LIBRARY_TABLE_STATE } from './help-document-library-table.config';
import { HelpPageService } from '../help-page.service';
import { ConfirmationDialogComponent } from 'app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { DataTableActionEnum, DataTableColumnFiltersApi, DataTableColumnModel, DataTableConfigSettings, DataTableStateModel } from 'app/shared/components/dynamic-table/dynamic-table.model';
import { BlobStorageService } from 'app/shared/services/blob-storage.service';
import { finalize, Subject, take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { HelperService } from 'app/shared/services/helper.service';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';
import { ListResponse } from 'app/shared/models/list-response.model';
import { DocumentLibraryEntry } from '../help-page.model';

@Component({
  selector: 'app-help-document-library',
  standalone: true,
  imports: [HelpModalComponent, UploadAttachmentsComponent, CaseFormSectionContainerComponent, CommonModule, DynamicTableComponent, TranslateModule],
  templateUrl: './help-document-library.component.html',
  styleUrl: './help-document-library.component.scss',
  providers: [DatePipe],
})
export class HelpDocumentLibraryComponent implements OnInit {
  @Input() isAdmin!: boolean;
  @ViewChild('uploadAttachmentsTemplate', { static: true }) uploadTemplate!: TemplateRef<any>;
  @ViewChild('actionsTemplate', { static: true }) actionsTemplate!: TemplateRef<any>;
  onAttachmentsChange$ = new Subject<any[]>();
  isModalOpen = false;
  columns = this.helperService.deepCopy(DOCUMENT_LIBRARY_TABLE_CONFIG);
  cases: any = [];
  attachments: any[] = [];
  filters: DataTableColumnFiltersApi[] = [];
  datatableStateFromStorage = this.helperService.getGridFilterObjectFromSessionStorage(DOCUMENT_LIBRARY_TABLE_STATE.gridName);
  dataTableState: DataTableStateModel = this.datatableStateFromStorage ? this.datatableStateFromStorage : { ...DOCUMENT_LIBRARY_TABLE_STATE };
  isDataTableReady: boolean = false;
  defaultColumns = this.helperService.deepCopy(DOCUMENT_LIBRARY_TABLE_CONFIG);
  selectedFilterName = '';

  constructor(
    private modalService: NgbModal,
    private helpPageService: HelpPageService,
    private blobStorageService: BlobStorageService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private configurationService: ConfigurationService,
    private helperService: HelperService
  ) { }

  ngOnInit(): void {
    this.getDataTableConfiguration();
  }

  fetchDocuments(): void {
    this.helpPageService.getDocuments(this.dataTableState).subscribe((res: ListResponse<DocumentLibraryEntry[]>) => {
      this.isDataTableReady = true;
      this.cases = res.items;
    });
  }

  addNewDocument(): void {
    const modalRef = this.modalService.open(HelpModalComponent, { centered: true, size: 'lg', windowClass: 'help-modal' });

    modalRef.componentInstance.title = this.translate.instant('label.upload_new_document');
    modalRef.componentInstance.cancelButtonText = this.translate.instant('label.cancel');
    modalRef.componentInstance.saveButtonText = this.translate.instant('buttons.upload');
    modalRef.componentInstance.modalBody = this.getUploadTemplate();

    const uploadedBlobIds: string[] = [];

    const subscription = this.onAttachmentsChange$.subscribe((attachments) => {
      attachments.forEach((attachment) => {
        if (!uploadedBlobIds.includes(attachment.blobStorageId)) {
          uploadedBlobIds.push(attachment.blobStorageId);
        }
      });
    });

    modalRef.result
      .then((result) => {
        if (result) {
          subscription.unsubscribe();
          this.saveDocument();
        }
      })
      .catch(() => {
        subscription.unsubscribe();
        this.deleteTemporaryFiles(uploadedBlobIds);
      });
  }

  deleteTemporaryFiles(blobIds: string[]): void {
    const validBlobIds = blobIds.filter((id) => id);

    validBlobIds.forEach((blobId) => {
      this.blobStorageService.deleteFile(blobId).subscribe(() => {
      });
    });
  }


  onLinkedValueClick(row: any): void {
    const file: DocumentLibraryEntry = row.row;
    if (!file?.storageFileId) {
      this.toastr.show(this.translate.instant('messages.document_download_error'), ToastTypesEnum.ERROR);
      return;
    }

    this.blobStorageService.downloadFile(file.storageFileId).subscribe((blob) => {
      this.helperService.saveFile(file, blob);
    }, () => {
      this.toastr.show(this.translate.instant('messages.document_download_error'), ToastTypesEnum.ERROR);
    });
  }

  saveDocument(): void {
    if (this.attachments.length > 0) {
      const attachment = this.attachments[0];

      if (!attachment.name || !attachment.blobStorageId || !attachment.type) {
        return;
      }

      const newDocument = {
        fileName: attachment.name,
        storageFileId: attachment.blobStorageId,
        mimeType: attachment.type,
      };

      this.helpPageService.addDocument(newDocument).subscribe(() => {
        const successMessage = this.translate.instant('messages.document_uploaded_success');
        this.toastr.show(successMessage, ToastTypesEnum.SUCCESS);
        this.fetchDocuments();
      });
      this.attachments = [];
    } else {
      const errorMessage = this.translate.instant('messages.document_upload_error');
      this.toastr.show(errorMessage, ToastTypesEnum.ERROR);
    }
  }

  deleteDocumentAndFile(documentId: number, blobStorageId: string): void {
    this.helpPageService.deleteDocument(documentId).subscribe(() => {
      this.blobStorageService.deleteFile(blobStorageId).subscribe(() => {
        const successMessage = this.translate.instant('messages.document_deleted_success');
        this.toastr.show(successMessage, ToastTypesEnum.SUCCESS);
        this.fetchDocuments();
      });
    });
  }

  actionButton(data: any): void {
    if (data.action === DataTableActionEnum.DELETE) {
      const modalRef = this.modalService.open(ConfirmationDialogComponent, {
        backdrop: 'static',
        keyboard: false,
        windowClass: 'confirmation-modal',
      });

      const titleText = data.row.name ? data.row.name : 'label.delete_document';
      modalRef.componentInstance.title = `${titleText}`;
      modalRef.componentInstance.message = 'messages.are_you_sure_you_want_to_delete_this_document';
      modalRef.componentInstance.notConfirmActionLabel = 'label.cancel';
      modalRef.componentInstance.confirmActionLabel = 'label.confirm';

      modalRef.result.then((result) => {
        if (result) {
          if (data.row.id) {
            this.deleteDocumentAndFile(data.row.id, data.row.storageFileId);
          } else {
            this.cases.splice(data.rowIndex, 1);
            this.cases = [...this.cases];
          }
        }
      });
    }
  }

  getUploadTemplate(): TemplateRef<any> {
    return this.uploadTemplate;
  }

  onAttachmentsChange(updatedAttachments: any[]): void {
    this.attachments = updatedAttachments;
    const validAttachments = updatedAttachments.filter((attachment) => attachment.blobStorageId);
    this.onAttachmentsChange$.next(validAttachments);
  }

  getDataTableConfiguration() {
    this.configurationService.getInitDataTableConfiguration(this.dataTableState.gridName)
      .pipe(
        take(1),
        finalize(() => {
          this.fetchDocuments();
        })
      )
      .subscribe((res: DataTableConfigSettings) => {
        if (this.datatableStateFromStorage) {
          this.columns = this.dataTableState.datatableColumns;
          this.filters = this.dataTableState.datatableFilters ?? [];
        } else {
          if (res && res.gridColumnSettings && !!JSON.parse(res.gridColumnSettings).length) {
            this.columns = JSON.parse(res.gridColumnSettings);
            this.dataTableState.datatableColumns = JSON.parse(res.gridColumnSettings);
          }

          if (res && res.gridFilterSettings && !!JSON.parse(res.gridFilterSettings).length) {
            this.filters = JSON.parse(res.gridFilterSettings);
            this.dataTableState.datatableFilters = JSON.parse(res.gridFilterSettings);
          }
        }

        if (res && res.name) {
          this.selectedFilterName = res.name;
        }

      });
  }

  onSaveDatatableSettings(data: DataTableStateModel) {
    const dataForSave = this.helperService.deepCopy(data);

    if (dataForSave && dataForSave.datatableColumns) {
      dataForSave.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    const params = {
      gridType: this.dataTableState.gridName,
      name: dataForSave.datatableFilterName || '',
      isGlobal: dataForSave.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForSave.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForSave.datatableColumns) || ''
    };

    this.configurationService.setDataTableConfiguration(params)
      .pipe(take(1))
      .subscribe();
  }

  onUpdateDatatableSettings(data: DataTableStateModel) {
    const dataForUpdate = this.helperService.deepCopy(data);

    if (dataForUpdate && dataForUpdate.datatableColumns) {
      dataForUpdate.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    const params = {
      gridType: this.dataTableState.gridName,
      name: dataForUpdate.datatableFilterName || '',
      isGlobal: dataForUpdate.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForUpdate.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForUpdate.datatableColumns) || ''
    };

    const filterId = dataForUpdate.datatableFilterId;

    this.configurationService.updateSavedFilter(filterId, params)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastr.show(this.translate.instant('messages.filter_successfully_updated'), ToastTypesEnum.SUCCESS);
        },
        error: (err) => {
          this.toastr.show(this.translate.instant('messages.failed_to_update_filter'), ToastTypesEnum.ERROR);
          console.error(err);
        }
      });
  }

  onDataTableStateChange(data: DataTableStateModel): void {
    this.dataTableState = data;
    this.fetchDocuments();
  }

}
